import { useState } from 'react'
import { useTestPlanRule, useTestPlanHistory } from '../../hooks/useTestPlanRule'
import toast from 'react-hot-toast'
import clsx from 'clsx'

const COVERAGE_TYPES = [
  { key: 'gen_role_based',  label: 'Role-based Test Plan', desc: 'Roles, responsibilities & scenario PDF', recommended: true },
  { key: 'gen_integration', label: 'Integration Tests',    desc: 'Cross-module and API interaction tests', recommended: true },
  { key: 'gen_unit',        label: 'Unit Tests',           desc: 'Individual function and component tests' },
  { key: 'gen_e2e',         label: 'End-to-End Tests',     desc: 'Full user journey scenarios' },
  { key: 'gen_full',        label: 'Full Coverage',        desc: 'All of the above in a single document' },
]

const COOLDOWN_OPTIONS = [
  { value: 0,    label: 'No cooldown' },
  { value: 30,   label: '30 minutes' },
  { value: 60,   label: '1 hour' },
  { value: 180,  label: '3 hours' },
  { value: 480,  label: '8 hours' },
  { value: 1440, label: '24 hours' },
]

const SOURCE_META = {
  manual_ui:    { label: 'Manual',       color: 'text-white/50 bg-white/6',              icon: '🖱' },
  manual_api:   { label: 'API',          color: 'text-white/50 bg-white/6',              icon: '⌨' },
  branch_gate:  { label: 'Branch gate',  color: 'text-amber-400 bg-amber-500/10',        icon: '⚡' },
}

const STATUS_META = {
  queued:     { color: 'text-blue-400 bg-blue-500/10',       label: 'Queued'     },
  generating: { color: 'text-purple-400 bg-purple-500/10',   label: 'Generating' },
  complete:   { color: 'text-emerald-400 bg-emerald-500/10', label: 'Complete'   },
  failed:     { color: 'text-red-400 bg-red-500/10',         label: 'Failed'     },
  skipped:    { color: 'text-white/35 bg-white/5',           label: 'Skipped'    },
}

/**
 * TestPlanRulePanel
 *
 * Full self-contained panel for configuring and triggering test plan generation.
 * Replaces the checkbox that was inside RepositoryAutomationPanel.
 *
 * Props:
 *   repositoryId   — number
 *   defaultBranch  — string  (used as placeholder for branch fields)
 *   repoFullName   — string  (display only)
 */
export default function TestPlanRulePanel({ repositoryId, defaultBranch = 'main', repoFullName }) {
  const { rule, loading, saving, error, save, trigger } = useTestPlanRule(repositoryId)
  const { history, loading: historyLoading, reload: reloadHistory } = useTestPlanHistory(repositoryId)

  const [form, setForm]             = useState(null)   // null = not dirty
  const [triggerBranch, setTriggerBranch] = useState('')
  const [triggering, setTriggering] = useState(false)
  const [tab, setTab]               = useState('settings') // 'settings' | 'history'

  // Initialise form from loaded rule
  const current = form ?? rule

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
    </div>
  )

  const isDirty = form !== null

  const set = (key, value) => setForm(prev => ({ ...(prev ?? rule), [key]: value }))

  // ── Save ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const result = await save(form)
    if (result.ok) {
      toast.success('Test plan rule saved.')
      setForm(null)
    } else {
      toast.error(result.error ?? 'Save failed.')
    }
  }

  // ── Manual trigger ────────────────────────────────────────────────────
  const handleTrigger = async () => {
    setTriggering(true)
    const branch = triggerBranch || defaultBranch
    const result = await trigger({ branch })

    if (result.triggered) {
      toast.success(`Test plan generation queued for branch "${branch}".`)
      reloadHistory()
    } else {
      toast.error(result.reason ?? 'Could not trigger generation.')
    }
    setTriggering(false)
  }

  const isBranchGate   = current?.trigger_mode === 'branch_gate'
  const inCooldown     = current?.in_cooldown
  const lastTriggered  = current?.last_triggered_human

  return (
    <div className="space-y-0 rounded-2xl overflow-hidden border border-white/8">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="px-5 py-4 bg-white/[0.02] border-b border-white/8 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-base">
            🧪
          </div>
          <div>
            <p className="text-sm font-bold text-white">Test Plan Generation</p>
            <p className="text-xs text-white/35 mt-0.5">
              {isBranchGate
                ? `Auto-triggers on ${current.gate_event} → ${current.gate_branch}`
                : 'Manual requests only'}
            </p>
          </div>
        </div>

        {/* Active / inactive toggle */}
        <button
          type="button"
          disabled={saving}
          onClick={() => {
            const updated = { ...(form ?? rule), is_active: !current.is_active }
            setForm(updated)
          }}
          className={clsx(
            'relative w-10 h-5 rounded-full transition-all flex-shrink-0',
            current?.is_active ? 'bg-amber-500' : 'bg-white/15',
            saving && 'opacity-50'
          )}
        >
          <div className={clsx(
            'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all',
            current?.is_active ? 'left-5' : 'left-0.5'
          )} />
        </button>
      </div>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <div className="flex border-b border-white/8">
        {[['settings', 'Settings'], ['history', `History (${history.length})`]].map(([t, label]) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={clsx(
              'px-5 py-2.5 text-xs font-semibold transition-colors border-b-2',
              tab === t
                ? 'text-amber-400 border-amber-400'
                : 'text-white/35 border-transparent hover:text-white/60'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Settings tab ───────────────────────────────────────────────── */}
      {tab === 'settings' && (
        <div className="p-5 space-y-6">

          {/* Trigger mode */}
          <div>
            <label className="text-xs font-bold text-white/50 uppercase tracking-widest block mb-3">
              How test plans are triggered
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                {
                  value: 'manual_only',
                  title: 'Manual only',
                  desc: 'A team member explicitly requests generation. Nothing runs automatically.',
                  icon: '🖱',
                },
                {
                  value: 'branch_gate',
                  title: 'Branch gate',
                  desc: 'Auto-trigger when a push or merge lands on a specific protected branch.',
                  icon: '⚡',
                },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('trigger_mode', opt.value)}
                  className={clsx(
                    'relative text-left p-4 rounded-xl border transition-all',
                    current?.trigger_mode === opt.value
                      ? 'border-amber-500/40 bg-amber-500/8'
                      : 'border-white/8 bg-white/[0.02] hover:border-white/16'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl">{opt.icon}</span>
                    <div>
                      <p className={clsx(
                        'text-sm font-bold',
                        current?.trigger_mode === opt.value ? 'text-amber-300' : 'text-white'
                      )}>
                        {opt.title}
                      </p>
                      <p className="text-xs text-white/40 mt-1 leading-relaxed">{opt.desc}</p>
                    </div>
                  </div>
                  {current?.trigger_mode === opt.value && (
                    <div className="absolute top-3 right-3 w-4 h-4 rounded-full bg-amber-500 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Branch gate config — only shown when branch_gate is selected */}
          {isBranchGate && (
            <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/15 space-y-4 animate-fade-in">
              <p className="text-xs font-bold text-amber-400/80 uppercase tracking-widest">Branch Gate Configuration</p>

              <div className="grid grid-cols-2 gap-4">
                {/* Gate branch */}
                <div>
                  <label className="text-xs text-white/40 font-medium block mb-1.5">Target branch</label>
                  <input
                    type="text"
                    value={current?.gate_branch ?? ''}
                    onChange={e => set('gate_branch', e.target.value)}
                    placeholder={`e.g. staging, release`}
                    className="input-specze w-full text-sm"
                  />
                  <p className="text-[11px] text-white/25 mt-1">
                    Test plans only generate when this exact branch receives a commit.
                  </p>
                </div>

                {/* Gate event */}
                <div>
                  <label className="text-xs text-white/40 font-medium block mb-1.5">Trigger on</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'merge', label: 'Merge / PR only', desc: 'Safer — only on approved merges' },
                      { value: 'push',  label: 'Any push',        desc: 'Every commit to this branch' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => set('gate_event', opt.value)}
                        className={clsx(
                          'flex-1 text-left p-3 rounded-lg border text-xs transition-all',
                          current?.gate_event === opt.value
                            ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
                            : 'border-white/8 text-white/40 hover:border-white/16'
                        )}
                      >
                        <p className="font-bold">{opt.label}</p>
                        <p className="text-[10px] mt-0.5 opacity-70">{opt.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Cooldown */}
              <div>
                <label className="text-xs text-white/40 font-medium block mb-1.5">Cooldown between auto-generations</label>
                <div className="flex flex-wrap gap-2">
                  {COOLDOWN_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('cooldown_minutes', opt.value)}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
                        current?.cooldown_minutes === opt.value
                          ? 'bg-amber-500/15 border-amber-500/35 text-amber-300'
                          : 'bg-white/3 border-white/8 text-white/40 hover:border-white/16'
                      )}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-white/25 mt-2">
                  Prevents duplicate generation if multiple commits land in quick succession.
                  Set to "No cooldown" only if you gate on merge (not push).
                </p>
              </div>

              {/* Cooldown status */}
              {inCooldown && current?.cooldown_remaining && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/20">
                  <span className="text-amber-400 text-sm">⏳</span>
                  <span className="text-xs text-amber-400/80">
                    Cooldown active — next auto-generation available in <strong>{current.cooldown_remaining}</strong>.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Coverage types */}
          <div>
            <label className="text-xs font-bold text-white/50 uppercase tracking-widest block mb-3">
              What to generate
            </label>
            <div className="space-y-2">
              {COVERAGE_TYPES.map(ct => (
                <label
                  key={ct.key}
                  className={clsx(
                    'flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all',
                    current?.[ct.key]
                      ? 'border-purple-500/30 bg-purple-500/6'
                      : 'border-white/6 bg-white/[0.02] hover:border-white/12'
                  )}
                >
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={!!current?.[ct.key]}
                      onChange={e => set(ct.key, e.target.checked)}
                    />
                    <div className={clsx(
                      'w-4 h-4 rounded border-2 flex items-center justify-center transition-all',
                      current?.[ct.key]
                        ? 'bg-purple-500 border-purple-500'
                        : 'border-white/20 bg-transparent'
                    )}>
                      {current?.[ct.key] && (
                        <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={clsx(
                        'text-sm font-semibold',
                        current?.[ct.key] ? 'text-white' : 'text-white/60'
                      )}>
                        {ct.label}
                      </span>
                      {ct.recommended && (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full border border-purple-500/20">
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-white/35 mt-0.5">{ct.desc}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-xs text-red-400">
              {error}
            </div>
          )}

          {/* Save bar */}
          {isDirty && (
            <div className="flex items-center justify-between gap-3 pt-2 border-t border-white/8 animate-fade-in">
              <p className="text-xs text-white/35">Unsaved changes</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setForm(null)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-white/40 hover:text-white transition-colors"
                >
                  Discard
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-xl bg-amber-500/20 border border-amber-500/35 text-amber-300 text-xs font-bold hover:bg-amber-500/30 transition-all disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save Rule'}
                </button>
              </div>
            </div>
          )}

          {/* ── Manual trigger section ──────────────────────────────────── */}
          <div className="border-t border-white/8 pt-5">
            <p className="text-xs font-bold text-white/50 uppercase tracking-widest mb-3">
              Generate Now
            </p>

            <div className="flex items-center gap-3">
              <input
                type="text"
                value={triggerBranch}
                onChange={e => setTriggerBranch(e.target.value)}
                placeholder={`Branch (default: ${defaultBranch})`}
                className="input-specze flex-1 text-sm"
              />
              <button
                type="button"
                onClick={handleTrigger}
                disabled={triggering || saving || isDirty}
                className={clsx(
                  'flex items-center gap-2 px-4 py-2.5 rounded-xl border text-xs font-bold transition-all flex-shrink-0',
                  triggering
                    ? 'bg-white/5 border-white/10 text-white/30 cursor-wait'
                    : isDirty
                    ? 'bg-white/3 border-white/8 text-white/20 cursor-not-allowed'
                    : 'bg-amber-500/15 border-amber-500/35 text-amber-300 hover:bg-amber-500/25'
                )}
              >
                {triggering ? (
                  <>
                    <div className="w-3.5 h-3.5 border border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
                    Queuing…
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                    Generate Test Plan
                  </>
                )}
              </button>
            </div>

            {isDirty && (
              <p className="text-[11px] text-white/30 mt-2">Save your rule changes before triggering.</p>
            )}

            {lastTriggered && (
              <p className="text-[11px] text-white/30 mt-2">
                Last triggered: <span className="text-white/45">{lastTriggered}</span>
                {current?.trigger_count > 0 && (
                  <> · {current.trigger_count} total generation{current.trigger_count !== 1 ? 's' : ''}</>
                )}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── History tab ─────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="divide-y divide-white/5">
          {historyLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-5 h-5 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-10 text-white/30 text-sm">
              No generation requests yet.
            </div>
          ) : (
            history.map(item => {
              const src    = SOURCE_META[item.trigger_source] ?? SOURCE_META.manual_ui
              const status = STATUS_META[item.status]         ?? STATUS_META.skipped

              return (
                <div key={item.id} className="px-5 py-3.5 flex items-center gap-3">
                  {/* Source badge */}
                  <span className={clsx('text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full flex-shrink-0', src.color)}>
                    {src.icon} {src.label}
                  </span>

                  {/* Branch + commit */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.branch && (
                        <code className="text-xs text-white/60 font-mono">{item.branch}</code>
                      )}
                      {item.commit_sha_short && (
                        <code className="text-[11px] text-white/30 font-mono">{item.commit_sha_short}</code>
                      )}
                      {item.coverage_types?.length > 0 && (
                        <span className="text-[10px] text-white/25">
                          {item.coverage_types.map(t => t.replace('_', ' ')).join(', ')}
                        </span>
                      )}
                    </div>
                    {item.skip_reason && (
                      <p className="text-[11px] text-white/25 mt-0.5 truncate">{item.skip_reason}</p>
                    )}
                    {item.requested_by && (
                      <p className="text-[11px] text-white/25 mt-0.5">by {item.requested_by}</p>
                    )}
                  </div>

                  {/* Status */}
                  <span className={clsx('text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full flex-shrink-0', status.color)}>
                    {status.label}
                  </span>

                  {/* Time */}
                  <span className="text-[11px] text-white/25 flex-shrink-0">{item.created_at_human}</span>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
